import { useQuery } from '@tanstack/react-query'
import { queryKeys } from './queryKeys'

export interface CreditTransaction {
  id: string
  accountId: string
  type: 'grant' | 'use' | 'purchase' | 'expire' | 'settlement' | 'trial_bonus' | 'rakurip_welcome_bonus' | 'referral_bonus'
  amount: number
  balanceAfter: number
  description: string | null
  sourceApp: string | null
  createdAt: string | null
}

export interface AccountPlan {
  accountId: string
  planId: 'trial' | 'basic' | 'standard' | 'premium'
  status: 'trial' | 'active' | 'past_due' | 'canceled'
  trialEndsAt: string | null
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  creditsPerMonth: number | null
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  billingInterval: 'monthly' | 'yearly' | null
  cancelAtPeriodEnd: boolean
  pendingPlanId: string | null
  pendingPlanChangesAt: string | null
}

export interface StripeInvoice {
  id: string
  number: string | null
  amountPaid: number
  currency: string
  status: string | null
  periodStart: string | null
  periodEnd: string | null
  createdAt: string
  invoicePdfUrl: string | null
  hostedInvoiceUrl: string | null
}

export interface CreditsData {
  balance: number
  displayBalance: number
  purchasedBalance: number
  plan: AccountPlan | null
  usage: {
    total: { usd: number; jpy: number }
    analysis: { usd: number; jpy: number; apiCalls: number }
    generation: { usd: number; jpy: number; apiCalls: number }
    text: { usd: number; jpy: number; apiCalls: number }
    tts: { usd: number; jpy: number; apiCalls: number }
    video: { usd: number; jpy: number; apiCalls: number }
  }
}

export function useCreditsQuery() {
  return useQuery({
    queryKey: queryKeys.credits.balance(),
    queryFn: async (): Promise<CreditsData | null> => {
      const res = await fetch('/api/account/credits', { credentials: 'include' })
      if (!res.ok) return null
      const data = await res.json()
      if (data.success && data.data) return data.data as CreditsData
      return null
    },
    refetchInterval: 30_000,  // 30秒ごとに更新
    staleTime: 10_000,
  })
}

export function useInvoicesQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['stripe', 'invoices'],
    queryFn: async (): Promise<StripeInvoice[]> => {
      const res = await fetch('/api/stripe/invoices', { credentials: 'include' })
      if (!res.ok) return []
      const data = await res.json()
      return data.success ? data.data : []
    },
    enabled,
    staleTime: 60_000,
  })
}
